import { useCallback, useEffect, useRef, useState } from "react";
import {
  useGlobalOverlayBasePath,
  useRoutedDialog,
} from "shared-ui/src/hooks/routedOverlays";
import type { BuyAgainResponse } from "@features/ecommerce/hooks/useBuyAgainOrder";
import { BUY_AGAIN_RESULT_DIALOG_SEGMENT } from "@features/ecommerce/constants/cartRouteSegments";

/**
 * Routes the buy-again result as a transient `{page}/buy-again-result` dialog so it records
 * a page view and closes on Back. Several instances can be mounted on one page (one per
 * shipment table or row button), so only the instance that captured a result opens.
 */
export default function useBuyAgainResultDialog(onClosed?: () => void) {
  const basePath = useGlobalOverlayBasePath(BUY_AGAIN_RESULT_DIALOG_SEGMENT);
  const dialog = useRoutedDialog({
    path: BUY_AGAIN_RESULT_DIALOG_SEGMENT,
    basePath,
    transient: true,
  });
  const [result, setResult] = useState<BuyAgainResponse | null>(null);

  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  const { open: openDialog, close: closeDialog, isOpen } = dialog;

  const show = useCallback(
    (next: BuyAgainResponse) => {
      setResult(next);
      openDialog();
    },
    [openDialog],
  );

  // Clears the result however the dialog closed (button or Back).
  const ownsOpenDialogRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      ownsOpenDialogRef.current = result != null;
      return;
    }
    if (!ownsOpenDialogRef.current) {
      return;
    }
    ownsOpenDialogRef.current = false;
    setResult(null);
    onClosedRef.current?.();
  }, [isOpen, result]);

  return {
    show,
    dialogProps: {
      open: isOpen && result != null,
      onClose: closeDialog,
      result,
    },
  };
}
